import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import { ProjectContext } from "./ProjectContext";
import { toast } from "react-toastify";

const UpdateProject = ({ setCurrentView }) => {
  const navigate = useNavigate();
  const { selectedProject, updateProject } = useContext(ProjectContext);
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      setFormData({
        title: selectedProject.title || "",
        techStack: selectedProject.tech_stack || [],
        collaborators: selectedProject.collaborators?.map((c) => ({
          name: c.name || "",
          description: c.description || c.role || "",
        })) || [],
        year: selectedProject.year?.toString() || "",
        location: selectedProject.location || "",
        link: selectedProject.link || "",
        logo: null,
        logo_background: null,
        video: null,
        contentBlocks: (selectedProject.description || []).map((block) => ({
          type: block.type || "text",
          content: block.content || "",
          url: block.url || "",
          description: block.description || "",
          file: null,
        })),
        is_featured: selectedProject.is_featured || false,
      });
    }
  }, [selectedProject]);

  const uploadFile = async (file) => {
    try {
      if (!file) throw new Error("No file Provided");
      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '')}`;
      const { error } = await supabase.storage
        .from("project-files")
        .upload(fileName, file, { contentType: file.type });
      if (error) {
        console.error("Upload Error:", JSON.stringify(error, null, 2));
        throw error;
      }
      const { data: { publicUrl } } = supabase.storage
        .from("project-files")
        .getPublicUrl(fileName);
      if (!publicUrl) throw new Error("Failed to get public URL");
      console.log("Uploaded File URL:", publicUrl);
      return publicUrl;
    } catch (err) {
      console.error("Upload Exception:", err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!formData.title.trim()) throw new Error("Project title is required.");

      const filesToUpload = [];
      if (formData.logo) filesToUpload.push({ key: "logo", file: formData.logo });
      if (formData.logo_background)
        filesToUpload.push({ key: "logo_background", file: formData.logo_background });
      if (formData.video) filesToUpload.push({ key: "video", file: formData.video });
      formData.contentBlocks.forEach((block, index) => {
        if ((block.type === "image" || block.type === "video") && block.file) {
          filesToUpload.push({ key: `contentBlocks[${index}].url`, file: block.file });
        }
      });

      const uploadedFiles = await Promise.all(
        filesToUpload.map(async ({ key, file }) => {
          const url = await uploadFile(file);
          return { key, url };
        })
      );

      const urlMap = {};
      uploadedFiles.forEach(({ key, url }) => {
        urlMap[key] = url;
      });

      const projectData = {
        title: formData.title,
        tech_stack: formData.techStack.filter((t) => t.trim()) || [],
        collaborators: formData.collaborators.filter(
          (c) => c.name.trim() && c.description.trim()
        ) || [],
        year: formData.year ? parseInt(formData.year) : null,
        location: formData.location || null,
        link: formData.link || null,
        logo: urlMap["logo"] || selectedProject.logo || null,
        logo_background: urlMap["logo_background"] || selectedProject.logo_background || null,
        video: urlMap["video"] || selectedProject.video || null,
        description: formData.contentBlocks.map((block, index) => {
          if (block.type === "text") {
            return { type: "text", content: block.content || "" };
          }
          return {
            type: block.type,
            url: urlMap[`contentBlocks[${index}].url`] || block.url || null,
            description: block.description || "",
          };
        }) || [],
        is_featured: formData.is_featured,
      };

      console.log("Submitting Project Data:", projectData);
      await updateProject(selectedProject.id, projectData);
      console.log("UpdateProject: Before toast.success");
      toast.success("Project updated successfully!", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-blue-600 text-white",
      });
      console.log("UpdateProject: After toast.success");
      setTimeout(() => {
        setCurrentView("read");
        navigate("/admin");
      }, 500);
    } catch (error) {
      console.error("Submission Error:", error);
      console.log("UpdateProject: Before toast.error");
      toast.error(`Error: ${error.message}`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-red-600 text-white",
      });
      console.log("UpdateProject: After toast.error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTech = () => {
    setFormData((prev) => ({ ...prev, techStack: [...prev.techStack, ""] }));
  };
  const removeTech = (index) => {
    setFormData((prev) => ({
      ...prev,
      techStack: prev.techStack.filter((_, i) => i !== index),
    }));
  };
  const handleTechChange = (index, value) => {
    setFormData((prev) => {
      const newTechStack = [...prev.techStack];
      newTechStack[index] = value;
      return { ...prev, techStack: newTechStack };
    });
  };

  const addCollaborator = () => {
    setFormData((prev) => ({
      ...prev,
      collaborators: [...prev.collaborators, { name: "", description: "" }],
    }));
  };
  const removeCollaborator = (index) => {
    setFormData((prev) => ({
      ...prev,
      collaborators: prev.collaborators.filter((_, i) => i !== index),
    }));
  };
  const handleCollaboratorChange = (index, field, value) => {
    setFormData((prev) => {
      const newCollaborators = [...prev.collaborators];
      newCollaborators[index][field] = value;
      return { ...prev, collaborators: newCollaborators };
    });
  };

  const addContentBlock = () => {
    setFormData((prev) => ({
      ...prev,
      contentBlocks: [
        ...prev.contentBlocks,
        { type: "text", content: "", file: null, description: "", url: "" },
      ],
    }));
  };
  const removeContentBlock = (index) => {
    setFormData((prev) => ({
      ...prev,
      contentBlocks: prev.contentBlocks.filter((_, i) => i !== index),
    }));
  };
  const handleContentBlockTypeChange = (index, type) => {
    setFormData((prev) => {
      const newContentBlocks = [...prev.contentBlocks];
      newContentBlocks[index] = {
        type,
        content: "",
        file: null,
        description: newContentBlocks[index].description || "",
        url: newContentBlocks[index].url || "",
      };
      return { ...prev, contentBlocks: newContentBlocks };
    });
  };
  const handleContentBlockChange = (index, field, value) => {
    setFormData((prev) => {
      const newContentBlocks = [...prev.contentBlocks];
      newContentBlocks[index][field] = value;
      return { ...prev, contentBlocks: newContentBlocks };
    });
  };
  const handleContentBlockFileChange = (index, file) => {
    setFormData((prev) => {
      const newContentBlocks = [...prev.contentBlocks];
      newContentBlocks[index].file = file;
      return { ...prev, contentBlocks: newContentBlocks };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (field, file) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
  };

  if (!formData) return <p className="text-center text-gray-600">Loading...</p>;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => 2000 + i);

  return (
    <section className="max-w-7xl mx-auto px-4 pb-20">
      <h2 className="text-4xl md:text-6xl font-extralight text-gray-900 text-center mb-12">
        Edit Project
      </h2>
      <form onSubmit={handleSubmit} className="bg-white p-8 max-w-3xl mx-auto">
        <div className="mb-6">
          <label htmlFor="title" className="block text-lg font-medium text-gray-700 mb-2">
            Project Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
            placeholder="Enter project title"
            required
          />
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Tech Stack</h3>
          {formData.techStack.map((tech, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={tech}
                onChange={(e) => handleTechChange(index, e.target.value)}
                className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
                placeholder="Enter technology"
              />
              <button
                type="button"
                onClick={() => removeTech(index)}
                className="ml-2 px-3 py-2 text-sm text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addTech}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Add Tech
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Collaborators</h3>
          {formData.collaborators.map((collab, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                placeholder="Name"
                value={collab.name}
                onChange={(e) => handleCollaboratorChange(index, "name", e.target.value)}
                className="w-1/2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 mr-2"
              />
              <input
                type="text"
                placeholder="Description"
                value={collab.description}
                onChange={(e) => handleCollaboratorChange(index, "description", e.target.value)}
                className="w-1/2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
              />
              <button
                type="button"
                onClick={() => removeCollaborator(index)}
                className="ml-2 px-3 py-2 text-sm text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addCollaborator}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Add Collaborator
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Details</h3>
          <div className="flex gap-4 mb-4">
            <div className="w-1/2">
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <select
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-1/2">
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
                placeholder="Enter location"
              />
            </div>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_featured"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-400 border-gray-300 rounded"
            />
            <label htmlFor="is_featured" className="ml-2 text-sm font-medium text-gray-700">
              Feature this project
            </label>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="link" className="block text-sm font-medium text-gray-700 mb-2">
            Project Link
          </label>
          <input
            type="text"
            id="link"
            name="link"
            value={formData.link}
            onChange={handleChange}
            className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
            placeholder="Enter project URL"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="logo" className="block text-sm font-medium text-gray-700 mb-2">
            Logo Image
          </label>
          {selectedProject.logo && (
            <img src={selectedProject.logo} alt="Current Logo" className="w-32 mb-2" />
          )}
          <input
            type="file"
            id="logo"
            accept="image/*"
            onChange={(e) => handleFileChange("logo", e.target.files[0])}
            className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="logo_background" className="block text-sm font-medium text-gray-700 mb-2">
            Logo Background Image
          </label>
          {selectedProject.logo_background && (
            <img src={selectedProject.logo_background} alt="Current Background" className="w-32 mb-2" />
          )}
          <input
            type="file"
            id="logo_background"
            accept="image/*"
            onChange={(e) => handleFileChange("logo_background", e.target.files[0])}
            className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="video" className="block text-sm font-medium text-gray-700 mb-2">
            Project Video
          </label>
          {selectedProject.video && (
            <video src={selectedProject.video} controls className="w-32 mb-2" />
          )}
          <input
            type="file"
            id="video"
            accept="video/*"
            onChange={(e) => handleFileChange("video", e.target.files[0])}
            className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
          />
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Description</h3>
          {formData.contentBlocks.map((block, index) => (
            <div key={index} className="mb-4 p-4 border rounded-lg">
              <div className="flex items-center mb-2">
                <select
                  value={block.type}
                  onChange={(e) => handleContentBlockTypeChange(index, e.target.value)}
                  className="w-1/3 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 mr-2"
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeContentBlock(index)}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                >
                  Remove Block
                </button>
              </div>
              {block.type === "text" ? (
                <textarea
                  placeholder="Content"
                  value={block.content || ""}
                  onChange={(e) => handleContentBlockChange(index, "content", e.target.value)}
                  className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
                  rows="4"
                />
              ) : (
                <>
                  {block.url && (
                    <div className="mb-2">
                      {block.type === "image" ? (
                        <img src={block.url} alt="Content" className="w-32" />
                      ) : (
                        <video src={block.url} controls className="w-32" />
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept={block.type === "image" ? "image/*" : "video/*"}
                    onChange={(e) => handleContentBlockFileChange(index, e.target.files[0])}
                    className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 mb-2"
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={block.description || ""}
                    onChange={(e) => handleContentBlockChange(index, "description", e.target.value)}
                    className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300"
                  />
                </>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addContentBlock}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Add Description Block
          </button>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              setCurrentView("read");
              navigate("/admin");
            }}
            className="px-6 py-3 bg-gray-600 text-white rounded hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Updating..." : "Update Project"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default UpdateProject;